import React, { useState, useEffect, useRef } from 'react';
import { useOrgData } from '../../contexts/OrgDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { DivisionKey, ExecutiveFunction } from '../../types/orgchart.types';
import './ExecutiveModal.css';

// Emojis grouped by industry/vertical
const emojiCategories: Record<string, { label: string; emojis: string[] }> = {
  general: {
    label: 'General Business',
    emojis: ['🏢', '🏗️', '🏭', '🏪', '💼', '📊', '🎯', '🚀', '💡', '🌟', '🏆', '✨', '⭐', '🔥', '💎', '👔']
  },
  tech: {
    label: 'Technology & Software',
    emojis: ['💻', '🖥️', '📱', '🤖', '🧠', '🔮', '⚡', '🛸', '🌐', '☁️', '🔒', '🛡️', '📡', '🎮', '🕹️', '💾']
  },
  finance: {
    label: 'Finance & Banking',
    emojis: ['🏦', '💰', '💵', '💳', '📈', '📉', '💹', '🪙', '💲', '🏧', '📑', '🧾', '💸', '🤑', '📋', '🔢']
  },
  healthcare: {
    label: 'Healthcare & Medical',
    emojis: ['🏥', '💊', '💉', '🩺', '🩻', '🧬', '🔬', '🧪', '❤️‍🩹', '🏨', '🚑', '⚕️', '🩸', '🦷', '👁️', '🧑‍⚕️']
  },
  education: {
    label: 'Education & Research',
    emojis: ['🏫', '🎓', '📚', '📖', '✏️', '🖊️', '📝', '🔎', '🧑‍🎓', '👨‍🏫', '📐', '🔭', '🧮', '📓', '🎒', '🏛️']
  },
  manufacturing: {
    label: 'Manufacturing & Industrial',
    emojis: ['🏭', '🔧', '⚙️', '🔩', '🛠️', '⛏️', '🔨', '🪛', '🧰', '🦺', '👷', '🏗️', '🚧', '🔌', '💡', '🔋']
  },
  retail: {
    label: 'Retail & Commerce',
    emojis: ['🛒', '🛍️', '🏬', '🏪', '🛎️', '🎁', '📦', '🏷️', '💳', '🧾', '👗', '👟', '💄', '🧴', '🛋️', '🪴']
  },
  food: {
    label: 'Food & Hospitality',
    emojis: ['🍽️', '🍴', '🥗', '🍕', '🍔', '☕', '🍷', '🏨', '🛎️', '👨‍🍳', '🍳', '🥂', '🍰', '🧁', '🍜', '🥡']
  },
  transport: {
    label: 'Transport & Logistics',
    emojis: ['🚚', '✈️', '🚢', '🚂', '🚁', '📦', '🛫', '🛬', '🚀', '🛤️', '⚓', '🚛', '🏎️', '🚌', '🛵', '🚲']
  },
  realestate: {
    label: 'Real Estate & Construction',
    emojis: ['🏠', '🏡', '🏘️', '🏙️', '🌆', '🌇', '🏗️', '🧱', '🪵', '🔑', '🚪', '🪟', '🏰', '⛪', '🕌', '🏯']
  },
  energy: {
    label: 'Energy & Utilities',
    emojis: ['⚡', '🔋', '☀️', '💨', '🌊', '⛽', '🛢️', '💧', '🔥', '♻️', '🌱', '🍃', '🌍', '🏔️', '🌋', '⛰️']
  },
  media: {
    label: 'Media & Entertainment',
    emojis: ['🎬', '🎥', '📺', '📻', '🎙️', '🎧', '🎵', '🎹', '🎸', '📰', '📸', '🖼️', '🎨', '🎭', '🎪', '🎡']
  },
  legal: {
    label: 'Legal & Government',
    emojis: ['⚖️', '🏛️', '📜', '🔏', '🗳️', '👨‍⚖️', '👩‍⚖️', '🚔', '🚨', '📋', '✍️', '🖋️', '🗃️', '📁', '🔐', '🏴']
  },
  agriculture: {
    label: 'Agriculture & Farming',
    emojis: ['🌾', '🚜', '🌻', '🌽', '🍎', '🍇', '🐄', '🐷', '🐔', '🌿', '🥕', '🍅', '🥬', '🧑‍🌾', '🏞️', '🌳']
  },
  sports: {
    label: 'Sports & Fitness',
    emojis: ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏋️', '🤸', '🧘', '🏃', '🚴', '🏊', '🥇', '🏅', '🎽', '👟']
  },
  travel: {
    label: 'Travel & Tourism',
    emojis: ['✈️', '🗺️', '🧳', '🏖️', '🏝️', '⛱️', '🎢', '🏕️', '🗼', '🗽', '🎡', '🌴', '🌊', '🏔️', '🌅', '🌄']
  }
};

interface ExecutiveModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExecutiveModal: React.FC<ExecutiveModalProps> = ({ isOpen, onClose }) => {
  const {
    orgData,
    updateCompanyVfp,
    updateExecutiveFunction,
    addExecutiveFunction,
    removeExecutiveFunction,
    reorderExecutiveFunction,
    updateAssignments,
    getEmployeeById,
    addEmployee,
    updateDivision
  } = useOrgData();
  const { organization, updateOrganizationName, updateOrganizationSettings } = useAuth();
  const { t, language } = useLocale();

  // Local state for form
  const [companyName, setCompanyName] = useState('');
  const [companyVfp, setCompanyVfp] = useState('');
  const [companyEmoji, setCompanyEmoji] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [execFunctions, setExecFunctions] = useState<ExecutiveFunction[]>([]);
  const [localAssignments, setLocalAssignments] = useState<Record<string, string[]>>({});
  const [localDivisionNames, setLocalDivisionNames] = useState<Record<string, string>>({});
  const [localDivisionVfps, setLocalDivisionVfps] = useState<Record<string, string>>({});
  const [localDivisionIcons, setLocalDivisionIcons] = useState<Record<string, string>>({});
  const [localDivisionColors, setLocalDivisionColors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasInitializedRef = useRef(false);

  // State for creating new employee inline
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState<string | null>(null); // funcId when showing form
  const [newEmployeeName, setNewEmployeeName] = useState('');

  // Load data from orgData when modal opens - only initialize ONCE per modal session
  useEffect(() => {
    if (!isOpen) {
      // Reset initialization flag when modal closes
      hasInitializedRef.current = false;
      return;
    }

    // Only initialize once when modal first opens
    if (isOpen && !hasInitializedRef.current) {
      hasInitializedRef.current = true;

      setCompanyName(organization?.name || '');
      setCompanyVfp(orgData.companyVfp || '');
      setCompanyEmoji(organization?.emoji || '');
      setCompanyLogo(organization?.logo || '');

      // Reset new employee form state
      setShowNewEmployeeForm(null);
      setNewEmployeeName('');

      // Load executive functions with defaults for Owner and CEO if names are empty
      const currentExecFuncs = orgData.executiveFunctions || [];
      const execFuncsWithDefaults = currentExecFuncs.map(func => {
        if (func.id === 'exec-owner' && !func.name) {
          return { ...func, name: t('exec.owner') };
        }
        if (func.id === 'exec-ceo' && !func.name) {
          return { ...func, name: t('exec.ceo') };
        }
        return func;
      });
      setExecFunctions(execFuncsWithDefaults);

      // Load assignments for executive functions
      const assignments: Record<string, string[]> = {};
      currentExecFuncs.forEach(func => {
        assignments[func.id] = orgData.assignments?.[func.id] || [];
      });
      setLocalAssignments(assignments);

      // Load division names, VFPs, icons, and colors
      const divNames: Record<string, string> = {};
      const divVfps: Record<string, string> = {};
      const divIcons: Record<string, string> = {};
      const divColors: Record<string, string> = {};
      Object.entries(orgData.divisions).forEach(([key, div]) => {
        divNames[key] = div.name;
        divVfps[key] = div.vfp || '';
        divIcons[key] = div.icon || '';
        divColors[key] = div.color || '';
      });
      setLocalDivisionNames(divNames);
      setLocalDivisionVfps(divVfps);
      setLocalDivisionIcons(divIcons);
      setLocalDivisionColors(divColors);
    }
  }, [isOpen, orgData, organization]);

  const handleSave = async () => {
    try {
      // Update company name in organization (Firebase)
      if (companyName && companyName !== organization?.name) {
        await updateOrganizationName(companyName);
      }

      // Update company emoji and logo (only if actually changed)
      if (companyEmoji !== (organization?.emoji || '') || companyLogo !== (organization?.logo || '')) {
        await updateOrganizationSettings({
          emoji: companyEmoji,
          logo: companyLogo,
        });
      }
    } catch (error: any) {
      alert(error.message || 'Failed to save company settings');
      return;
    }

    // Update company VFP in orgData
    updateCompanyVfp(companyVfp);

    // Update all executive functions
    execFunctions.forEach(func => {
      const existing = orgData.executiveFunctions?.find(f => f.id === func.id);
      if (existing) {
        updateExecutiveFunction(func.id, {
          name: func.name,
          managesDivisions: func.managesDivisions,
          sortOrder: func.sortOrder
        });
      } else {
        // New function
        addExecutiveFunction(func);
      }
      // Update assignments
      updateAssignments(func.id, localAssignments[func.id] || []);
    });

    // Remove deleted executive functions
    const currentIds = execFunctions.map(f => f.id);
    (orgData.executiveFunctions || []).forEach(func => {
      if (!currentIds.includes(func.id)) {
        removeExecutiveFunction(func.id);
      }
    });

    // Update division names, VFPs, icons, and colors
    Object.keys(orgData.divisions).forEach(key => {
      const divKey = key as DivisionKey;
      const updates: { name?: string; vfp?: string; icon?: string; color?: string } = {};

      if (localDivisionNames[divKey] !== undefined && orgData.divisions[divKey].name !== localDivisionNames[divKey]) {
        updates.name = localDivisionNames[divKey];
      }
      if (localDivisionVfps[divKey] !== undefined && (orgData.divisions[divKey].vfp || '') !== localDivisionVfps[divKey]) {
        updates.vfp = localDivisionVfps[divKey];
      }
      if (localDivisionIcons[divKey] !== undefined && (orgData.divisions[divKey].icon || '') !== localDivisionIcons[divKey]) {
        updates.icon = localDivisionIcons[divKey];
      }
      if (localDivisionColors[divKey] !== undefined && (orgData.divisions[divKey].color || '') !== localDivisionColors[divKey]) {
        updates.color = localDivisionColors[divKey];
      }

      if (Object.keys(updates).length > 0) {
        updateDivision(divKey, updates);
      }
    });

    onClose();
  };

  const addCLevel = () => {
    const newId = `exec-${Date.now()}`;
    // Get current C-level count for sortOrder
    const currentCLevels = execFunctions.filter(f => f.id !== 'exec-owner' && f.id !== 'exec-ceo');
    const newDeputyName = language === 'en' ? 'New Deputy' : language === 'ar' ? 'نائب جديد' : 'סמנכ"ל חדש';
    const newFunc: ExecutiveFunction = {
      id: newId,
      name: newDeputyName,
      managesDivisions: [],
      sortOrder: currentCLevels.length  // Add at the end
    };
    setExecFunctions([...execFunctions, newFunc]);
    setLocalAssignments({ ...localAssignments, [newId]: [] });
  };

  const removeCLevel = (id: string) => {
    // Don't allow removing CEO or Owner
    if (id === 'exec-ceo' || id === 'exec-owner') return;

    setExecFunctions(execFunctions.filter(f => f.id !== id));
    const newAssignments = { ...localAssignments };
    delete newAssignments[id];
    setLocalAssignments(newAssignments);
  };

  const updateExecFunc = (id: string, updates: Partial<ExecutiveFunction>) => {
    setExecFunctions(execFunctions.map(f =>
      f.id === id ? { ...f, ...updates } : f
    ));
  };

  const toggleDivisionForCLevel = (funcId: string, divKey: DivisionKey) => {
    setExecFunctions(execFunctions.map(f => {
      if (f.id !== funcId) return f;
      const has = f.managesDivisions.includes(divKey);
      return {
        ...f,
        managesDivisions: has
          ? f.managesDivisions.filter(d => d !== divKey)
          : [...f.managesDivisions, divKey]
      };
    }));
  };

  // Use functional updates to avoid closure issues with stale state
  const addAssignedEmployee = (funcId: string, employeeId: string) => {
    setLocalAssignments(prev => {
      const current = prev[funcId] || [];
      if (current.includes(employeeId)) return prev;
      return {
        ...prev,
        [funcId]: [...current, employeeId]
      };
    });
  };

  const removeAssignedEmployee = (funcId: string, employeeId: string) => {
    setLocalAssignments(prev => {
      const current = prev[funcId] || [];
      return {
        ...prev,
        [funcId]: current.filter(id => id !== employeeId)
      };
    });
  };

  // Just create the employee - user will select from dropdown to assign
  const handleCreateNewEmployee = () => {
    if (!newEmployeeName.trim()) return;

    const employeeName = newEmployeeName.trim();
    const ceo = orgData.employees.find(e => e.id === 'ceo' || e.id === '1');
    const parentId = ceo?.id || '1';

    addEmployee({
      name: employeeName,
      division: 'executive' as DivisionKey,
      parentId
    });

    // Reset form
    setShowNewEmployeeForm(null);
    setNewEmployeeName('');
  };

  // Get executive functions by type
  const ownerFunc = execFunctions.find(f => f.id === 'exec-owner');
  const ceoFunc = execFunctions.find(f => f.id === 'exec-ceo');
  const cLevelFuncs = execFunctions.filter(f =>
    f.id !== 'exec-owner' && f.id !== 'exec-ceo'
  );

  // Get all divisions for C-level management assignment
  const availableDivisions = Object.entries(orgData.divisions)
    .map(([key, div]) => ({ key: key as DivisionKey, name: div.name, number: div.number }))
    .sort((a, b) => b.number - a.number);

  // Get all employees for assignment dropdown
  const allEmployees = orgData.employees.filter(e =>
    e.id !== 'owner' && e.id !== '0' && e.id !== 'board'
  );

  if (!isOpen) return null;

  return (
    <div className="executive-modal-overlay" onClick={onClose}>
      <div className="executive-modal" onClick={e => e.stopPropagation()}>
        <div className="executive-modal-header">
          <h2>{t('exec.settings')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="executive-modal-content">
          {/* Company Info */}
          <div className="section-card">
            <h3>{t('exec.companyInfo')}</h3>
            <div className="form-row">
              <label>{t('exec.companyName')}</label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder={t('exec.companyName')}
              />
            </div>
            <div className="form-row">
              <label>{t('exec.companyVfp')}</label>
              <textarea
                value={companyVfp}
                onChange={e => setCompanyVfp(e.target.value)}
                placeholder={t('exec.companyVfp')}
                rows={2}
              />
            </div>
            <div className="form-row">
              <label>{language === 'en' ? 'Company Emoji' : language === 'ar' ? 'رمز الشركة' : 'אימוג\'י חברה'}</label>
              <select
                value={companyEmoji}
                onChange={(e) => setCompanyEmoji(e.target.value)}
                className="emoji-select"
              >
                <option value="">{language === 'en' ? 'Select an emoji...' : language === 'ar' ? 'اختر رمز...' : 'בחר אימוג\'י...'}</option>
                {Object.entries(emojiCategories).map(([key, { label, emojis }]) => (
                  <optgroup key={key} label={label}>
                    {emojis.map((emoji) => (
                      <option key={emoji} value={emoji}>{emoji}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>{language === 'en' ? 'Company Logo' : language === 'ar' ? 'شعار الشركة' : 'לוגו חברה'}</label>
              <div className="logo-upload-row">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setCompanyLogo(event.target?.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn-upload"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {language === 'en' ? 'Upload Logo' : language === 'ar' ? 'رفع الشعار' : 'העלה לוגו'}
                </button>
                {companyLogo && (
                  <button
                    type="button"
                    className="btn-clear"
                    onClick={() => setCompanyLogo('')}
                  >
                    {language === 'en' ? 'Clear' : language === 'ar' ? 'مسح' : 'נקה'}
                  </button>
                )}
              </div>
              {companyLogo && (
                <div className="logo-preview">
                  <img src={companyLogo} alt="Logo preview" />
                </div>
              )}
            </div>
          </div>

          {/* Divisions - moved before hierarchy */}
          <div className="section-card">
            <h3>{language === 'en' ? 'Divisions' : language === 'ar' ? 'الأقسام' : 'חטיבות'}</h3>
            <div className="divisions-names-grid">
              {availableDivisions.map(div => (
                <div key={div.key} className="division-edit-card" style={{ borderLeftColor: localDivisionColors[div.key] || orgData.divisions[div.key]?.color || '#ccc' }}>
                  <div className="division-edit-header">
                    <span className="division-number">{div.number}.</span>
                    <input
                      type="text"
                      value={localDivisionNames[div.key] !== undefined ? localDivisionNames[div.key] : (div.name || '')}
                      onChange={e => setLocalDivisionNames(prev => ({
                        ...prev,
                        [div.key]: e.target.value
                      }))}
                      placeholder={div.name || (language === 'en' ? 'Division name' : language === 'ar' ? 'اسم القسم' : 'שם החטיבה')}
                      className="division-name-input"
                    />
                    <input
                      type="text"
                      value={localDivisionIcons[div.key] !== undefined ? localDivisionIcons[div.key] : (orgData.divisions[div.key]?.icon || '')}
                      onChange={e => setLocalDivisionIcons(prev => ({
                        ...prev,
                        [div.key]: e.target.value
                      }))}
                      placeholder="📁"
                      className="division-icon-input"
                      maxLength={2}
                    />
                    <input
                      type="color"
                      value={localDivisionColors[div.key] || orgData.divisions[div.key]?.color || '#667eea'}
                      onChange={e => setLocalDivisionColors(prev => ({
                        ...prev,
                        [div.key]: e.target.value
                      }))}
                      className="division-color-input"
                    />
                  </div>
                  <div className="division-vfp-row">
                    <label>{language === 'en' ? 'VFP:' : language === 'ar' ? 'المنتج:' : 'מוצר:'}</label>
                    <textarea
                      value={localDivisionVfps[div.key] !== undefined ? localDivisionVfps[div.key] : (orgData.divisions[div.key]?.vfp || '')}
                      onChange={e => setLocalDivisionVfps(prev => ({
                        ...prev,
                        [div.key]: e.target.value
                      }))}
                      placeholder={orgData.divisions[div.key]?.vfp || (language === 'en' ? 'Valuable Final Product' : language === 'ar' ? 'المنتج النهائي القيم' : 'מוצר סופי בעל ערך')}
                      className="division-vfp-input"
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* High Level Hierarchy Section */}
          <div className="section-card hierarchy-section">
            <h3>{language === 'en' ? 'Executive Hierarchy' : language === 'ar' ? 'التسلسل الهرمي التنفيذي' : 'היררכיה ניהולית'}</h3>

            {/* Owner */}
            {ownerFunc && (
              <div className="hierarchy-card">
                <h4>{t('exec.owner')}</h4>
              <div className="form-row">
                <label>{t('exec.functionName')}</label>
                <input
                  type="text"
                  value={ownerFunc.name || t('exec.owner')}
                  onChange={e => updateExecFunc('exec-owner', { name: e.target.value })}
                  placeholder={t('exec.owner')}
                />
              </div>
              <div className="form-row">
                <label>{t('edit.employee')}</label>
                <div className="assigned-employees-list">
                  {(localAssignments['exec-owner'] || []).map(empId => {
                    const emp = getEmployeeById(empId);
                    return emp ? (
                      <span key={empId} className="assigned-badge">
                        {emp.name}
                        <button type="button" onClick={() => removeAssignedEmployee('exec-owner', empId)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {(localAssignments['exec-owner'] || []).length === 0 && (
                    <span className="no-assignment">{t('edit.noEmployee')}</span>
                  )}
                </div>
                {showNewEmployeeForm === 'exec-owner' ? (
                  <div className="new-employee-form">
                    <input
                      type="text"
                      value={newEmployeeName}
                      onChange={e => setNewEmployeeName(e.target.value)}
                      placeholder={language === 'en' ? 'Employee name' : language === 'ar' ? 'اسم الموظف' : 'שם העובד'}
                      autoFocus
                    />
                    <div className="new-employee-actions">
                      <button
                        type="button"
                        className="btn-create-employee"
                        onClick={handleCreateNewEmployee}
                        disabled={!newEmployeeName.trim()}
                      >
                        {language === 'en' ? 'Create' : language === 'ar' ? 'إنشاء' : 'צור'}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-new"
                        onClick={() => {
                          setShowNewEmployeeForm(null);
                          setNewEmployeeName('');
                        }}
                      >
                        {t('general.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="employee-select-row">
                    <select
                      value=""
                      onChange={e => {
                        if (e.target.value) {
                          addAssignedEmployee('exec-owner', e.target.value);
                        }
                      }}
                    >
                      <option value="">{t('employees.add')}</option>
                      {allEmployees
                        .filter(emp => !(localAssignments['exec-owner'] || []).includes(emp.id))
                        .map(emp => (
                          <option key={emp.id} value={emp.id}>{emp.name}</option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="btn-new-employee"
                      onClick={() => setShowNewEmployeeForm('exec-owner')}
                    >
                      {language === 'en' ? '+ New' : language === 'ar' ? '+ جديد' : '+ חדש'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

            {/* CEO */}
            {ceoFunc && (
              <div className="hierarchy-card">
                <h4>{t('exec.ceo')}</h4>
              <div className="form-row">
                <label>{t('exec.functionName')}</label>
                <input
                  type="text"
                  value={ceoFunc.name || t('exec.ceo')}
                  onChange={e => updateExecFunc('exec-ceo', { name: e.target.value })}
                  placeholder={t('exec.ceo')}
                />
              </div>
              <div className="form-row">
                <label>{t('edit.employee')}</label>
                <div className="assigned-employees-list">
                  {(localAssignments['exec-ceo'] || []).map(empId => {
                    const emp = getEmployeeById(empId);
                    return emp ? (
                      <span key={empId} className="assigned-badge">
                        {emp.name}
                        <button type="button" onClick={() => removeAssignedEmployee('exec-ceo', empId)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {(localAssignments['exec-ceo'] || []).length === 0 && (
                    <span className="no-assignment">{t('edit.noEmployee')}</span>
                  )}
                </div>
                {showNewEmployeeForm === 'exec-ceo' ? (
                  <div className="new-employee-form">
                    <input
                      type="text"
                      value={newEmployeeName}
                      onChange={e => setNewEmployeeName(e.target.value)}
                      placeholder={language === 'en' ? 'Employee name' : language === 'ar' ? 'اسم الموظف' : 'שם העובד'}
                      autoFocus
                    />
                    <div className="new-employee-actions">
                      <button
                        type="button"
                        className="btn-create-employee"
                        onClick={handleCreateNewEmployee}
                        disabled={!newEmployeeName.trim()}
                      >
                        {language === 'en' ? 'Create' : language === 'ar' ? 'إنشاء' : 'צור'}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-new"
                        onClick={() => {
                          setShowNewEmployeeForm(null);
                          setNewEmployeeName('');
                        }}
                      >
                        {t('general.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="employee-select-row">
                    <select
                      value=""
                      onChange={e => {
                        if (e.target.value) {
                          addAssignedEmployee('exec-ceo', e.target.value);
                        }
                      }}
                    >
                      <option value="">{t('employees.add')}</option>
                      {allEmployees
                        .filter(emp => !(localAssignments['exec-ceo'] || []).includes(emp.id))
                        .map(emp => (
                          <option key={emp.id} value={emp.id}>{emp.name}</option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="btn-new-employee"
                      onClick={() => setShowNewEmployeeForm('exec-ceo')}
                    >
                      {language === 'en' ? '+ New' : language === 'ar' ? '+ جديد' : '+ חדש'}
                    </button>
                  </div>
                )}
              </div>
              <div className="divisions-select">
                <label>{language === 'en' ? 'Directly managed divisions:' : language === 'ar' ? 'الأقسام تحت الإدارة المباشرة:' : 'חטיבות תחת ניהול ישיר:'}</label>
                <div className="divisions-chips">
                  {availableDivisions.map(div => (
                    <button
                      key={div.key}
                      className={`division-chip ${ceoFunc.managesDivisions?.includes(div.key) ? 'selected' : ''}`}
                      onClick={() => toggleDivisionForCLevel('exec-ceo', div.key)}
                    >
                      {div.number}. {div.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

            {/* C-Levels / Deputies */}
            <div className="hierarchy-card deputies-card">
              <div className="deputies-header">
                <h4>{t('exec.deputies')}</h4>
                <button className="add-btn" onClick={addCLevel}>{t('exec.addExecutive')}</button>
              </div>

            {cLevelFuncs.map((func, index) => {
              // Compute role label from managed divisions
              const managedDivNames = func.managesDivisions
                .map(divKey => orgData.divisions[divKey]?.name)
                .filter(Boolean);
              const deputyLabel = language === 'en' ? 'Deputy' : language === 'ar' ? 'نائب' : 'סמנכ״ל';
              const selectDivisionsLabel = language === 'en' ? '(select divisions)' : language === 'ar' ? '(اختر الأقسام)' : '(בחר חטיבות)';
              const roleLabel = managedDivNames.length > 0
                ? `${deputyLabel} ${managedDivNames.join(', ')}`
                : `${deputyLabel} ${selectDivisionsLabel}`;

              const moveUp = () => {
                if (index === 0) return;
                const newFuncs = [...execFunctions];
                const cLevelStartIndex = newFuncs.findIndex(f => f.id !== 'exec-owner' && f.id !== 'exec-ceo');
                const actualIndex = cLevelStartIndex + index;
                [newFuncs[actualIndex], newFuncs[actualIndex - 1]] = [newFuncs[actualIndex - 1], newFuncs[actualIndex]];
                // Update sortOrder for all C-levels
                newFuncs.filter(f => f.id !== 'exec-owner' && f.id !== 'exec-ceo').forEach((f, i) => {
                  f.sortOrder = i;
                });
                setExecFunctions(newFuncs);
              };

              const moveDown = () => {
                if (index === cLevelFuncs.length - 1) return;
                const newFuncs = [...execFunctions];
                const cLevelStartIndex = newFuncs.findIndex(f => f.id !== 'exec-owner' && f.id !== 'exec-ceo');
                const actualIndex = cLevelStartIndex + index;
                [newFuncs[actualIndex], newFuncs[actualIndex + 1]] = [newFuncs[actualIndex + 1], newFuncs[actualIndex]];
                // Update sortOrder for all C-levels
                newFuncs.filter(f => f.id !== 'exec-owner' && f.id !== 'exec-ceo').forEach((f, i) => {
                  f.sortOrder = i;
                });
                setExecFunctions(newFuncs);
              };

              return (
                <div key={func.id} className="clevel-card">
                  <div className="clevel-header">
                    <div className="reorder-buttons">
                      <button
                        type="button"
                        className="reorder-btn"
                        onClick={moveUp}
                        disabled={index === 0}
                      >▲</button>
                      <button
                        type="button"
                        className="reorder-btn"
                        onClick={moveDown}
                        disabled={index === cLevelFuncs.length - 1}
                      >▼</button>
                    </div>
                    <span className="clevel-role-label">{roleLabel}</span>
                    <button className="remove-btn" onClick={() => removeCLevel(func.id)}>🗑️</button>
                  </div>
                  <div className="form-row">
                    <label>{t('exec.functionName')}</label>
                    <input
                      type="text"
                      value={func.name}
                      onChange={e => updateExecFunc(func.id, { name: e.target.value })}
                      placeholder={t('exec.functionName')}
                      className="name-input"
                    />
                  </div>
                  <div className="form-row">
                    <label>{t('edit.employee')}</label>
                    <div className="assigned-employees-list">
                      {(localAssignments[func.id] || []).map(empId => {
                        const emp = getEmployeeById(empId);
                        return emp ? (
                          <span key={empId} className="assigned-badge">
                            {emp.name}
                            <button type="button" onClick={() => removeAssignedEmployee(func.id, empId)}>×</button>
                          </span>
                        ) : null;
                      })}
                      {(localAssignments[func.id] || []).length === 0 && (
                        <span className="no-assignment">{t('edit.noEmployee')}</span>
                      )}
                    </div>
                    {showNewEmployeeForm === func.id ? (
                      <div className="new-employee-form">
                        <input
                          type="text"
                          value={newEmployeeName}
                          onChange={e => setNewEmployeeName(e.target.value)}
                          placeholder={language === 'en' ? 'Employee name' : language === 'ar' ? 'اسم الموظف' : 'שם העובד'}
                          autoFocus
                        />
                        <div className="new-employee-actions">
                          <button
                            type="button"
                            className="btn-create-employee"
                            onClick={handleCreateNewEmployee}
                            disabled={!newEmployeeName.trim()}
                          >
                            {language === 'en' ? 'Create' : language === 'ar' ? 'إنشاء' : 'צור'}
                          </button>
                          <button
                            type="button"
                            className="btn-cancel-new"
                            onClick={() => {
                              setShowNewEmployeeForm(null);
                              setNewEmployeeName('');
                            }}
                          >
                            {t('general.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="employee-select-row">
                        <select
                          value=""
                          onChange={e => {
                            if (e.target.value) {
                              addAssignedEmployee(func.id, e.target.value);
                            }
                          }}
                        >
                          <option value="">{t('employees.add')}</option>
                          {allEmployees
                            .filter(emp => !(localAssignments[func.id] || []).includes(emp.id))
                            .map(emp => (
                              <option key={emp.id} value={emp.id}>{emp.name}</option>
                            ))}
                        </select>
                        <button
                          type="button"
                          className="btn-new-employee"
                          onClick={() => setShowNewEmployeeForm(func.id)}
                        >
                          {language === 'en' ? '+ New' : language === 'ar' ? '+ جديد' : '+ חדש'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="divisions-select">
                    <label>{language === 'en' ? 'Managed divisions:' : language === 'ar' ? 'الأقسام المُدارة:' : 'חטיבות תחת ניהול:'}</label>
                    <div className="divisions-chips">
                      {availableDivisions.map(div => (
                        <button
                          key={div.key}
                          className={`division-chip ${func.managesDivisions.includes(div.key) ? 'selected' : ''}`}
                          onClick={() => toggleDivisionForCLevel(func.id, div.key)}
                        >
                          {div.number}. {div.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

              {cLevelFuncs.length === 0 && (
                <div className="empty-state">
                  {language === 'en' ? 'No deputies defined. Click "Add Deputy" to add one.' : language === 'ar' ? 'لا يوجد نواب محددين. انقر "إضافة نائب" للإضافة.' : 'אין סמנכ"לים מוגדרים. לחץ "הוסף סמנכ"ל" להוספה.'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="executive-modal-footer">
          <div className="form-buttons">
            <button className="btn-primary" onClick={handleSave}>{t('general.save')}</button>
            <button className="btn-secondary" onClick={onClose}>{t('general.cancel')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};
